"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useRef } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { CompactMenu, type CompactMenuItem } from "@/components/compact-menu";
import { TabToggle } from "@/components/common/TabToggle";
import { useSearchParams } from "next/navigation";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { useWaveCurations } from "@/hooks/waves/useWaveCurations";
import { useWave } from "@/hooks/useWave";
import { useDecisionPoints } from "@/hooks/waves/useDecisionPoints";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { Time } from "@/helpers/time";
import { useAuth } from "@/components/auth/Auth";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useContentTab, WaveVotingState, type SetActiveContentTab } from "../ContentTabContext";
import MyStreamWaveCreateCurationAction from "./tabs/MyStreamWaveCreateCurationAction";
interface MyStreamWaveDesktopTabsProps {
  readonly activeTab: MyStreamWaveTab;
  readonly wave: ApiWave;
  readonly setActiveTab: SetActiveContentTab;
  readonly activeCurationId: string | null;
  readonly onSelectCuration: (curationId: string | null) => void;
  readonly showCreateCurationAction?: boolean | undefined;
}
interface TabOption {
  readonly key: string;
  readonly label: string;
  readonly panelId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const getCurationPanelId = (curationId: string): string => `my-stream-wave-tabpanel-curation-${curationId}`;
const AUTO_EXPAND_LIMIT = 5;
const MOBILE_INLINE_CURATION_LIMIT = 1;
const TAB_LABELS: Record<MyStreamWaveTab, string> = {
  [MyStreamWaveTab.CHAT]: "Chat",
  [MyStreamWaveTab.LEADERBOARD]: "Leaderboard",
  [MyStreamWaveTab.SALES]: "Sales",
  [MyStreamWaveTab.WINNERS]: "Winners",
  [MyStreamWaveTab.OUTCOME]: "Outcome",
  [MyStreamWaveTab.MY_VOTES]: "My Votes",
  [MyStreamWaveTab.FAQ]: "FAQ"
};
const getWaveVotingState = ({
  isUpcoming,
  isCompleted
}: {
  readonly isUpcoming: boolean;
  readonly isCompleted: boolean;
}): WaveVotingState => {
  if (isUpcoming) {
    return WaveVotingState.NOT_STARTED;
  }
  if (isCompleted) {
    return WaveVotingState.ENDED;
  }
  return WaveVotingState.ONGOING;
};
const MyStreamWaveDesktopTabs: React.FC<MyStreamWaveDesktopTabsProps> = t0 => {
  const $ = _c(109);
  const {
    activeTab,
    wave,
    setActiveTab,
    activeCurationId,
    onSelectCuration,
    showCreateCurationAction: t1
  } = t0;
  const showCreateCurationAction = t1 === undefined ? true : t1;
  const searchParams = useSearchParams();
  const {
    availableTabs,
    updateAvailableTabs,
    setActiveContentTab
  } = useContentTab();
  const {
    connectedProfile
  } = useAuth();
  const hasAuthenticatedProfile = Boolean(connectedProfile?.handle);
  const {
    isChatWave,
    isMemesWave,
    isCurationWave,
    pauses: t2
  } = useWave(wave);
  const {
    filterDecisionsDuringPauses
  } = t2;
  const {
    voting: t3,
    decisions: t4
  } = useWaveTimers(wave);
  const {
    isUpcoming,
    isCompleted
  } = t3;
  const {
    firstDecisionDone
  } = t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      initialPastWindow: 3,
      initialFutureWindow: 10
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const {
    allDecisions,
    hasMoreFuture,
    loadMoreFuture
  } = useDecisionPoints(wave, t5);
  let t6;
  if ($[1] !== wave.id) {
    t6 = {
      waveId: wave.id
    };
    $[1] = wave.id;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const {
    data: t7
  } = useWaveCurations(t6);
  let t8;
  if ($[3] !== t7) {
    t8 = t7 === undefined ? [] : t7;
    $[3] = t7;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const curations = t8;
  const canManageCurations = wave.wave.authenticated_user_eligible_for_admin === true;
  let t9;
  if ($[5] !== allDecisions || $[6] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t9 = allDecisions.filter(decision_0 => filtered.some(item => item.decision_time === decision_0.timestamp));
    $[5] = allDecisions;
    $[6] = filterDecisionsDuringPauses;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const filteredDecisions = t9;
  let t10;
  if ($[8] !== filteredDecisions) {
    t10 = filteredDecisions.find(_temp2)?.timestamp ?? null;
    $[8] = filteredDecisions;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const nextDecisionTime = t10;
  const autoExpandFutureAttemptsRef = useRef(0);
  const desktopTabsScrollerRef = useRef(null);
  const mobileTabsScrollerRef = useRef(null);
  let t11;
  let t12;
  if ($[10] !== hasMoreFuture || $[11] !== loadMoreFuture || $[12] !== nextDecisionTime) {
    t11 = () => {
      const hasUpcoming = typeof nextDecisionTime === "number";
      if (hasUpcoming || !hasMoreFuture) {
        autoExpandFutureAttemptsRef.current = 0;
        return;
      }
      if (autoExpandFutureAttemptsRef.current >= AUTO_EXPAND_LIMIT) {
        return;
      }
      const timeoutId = globalThis.setTimeout(() => {
        autoExpandFutureAttemptsRef.current = autoExpandFutureAttemptsRef.current + 1;
        loadMoreFuture();
      }, 50);
      return () => {
        clearTimeout(timeoutId);
      };
    };
    t12 = [nextDecisionTime, hasMoreFuture, loadMoreFuture];
    $[10] = hasMoreFuture;
    $[11] = loadMoreFuture;
    $[12] = nextDecisionTime;
    $[13] = t11;
    $[14] = t12;
  } else {
    t11 = $[13];
    t12 = $[14];
  }
  useEffect(t11, t12);
  let t13;
  if ($[15] !== isCompleted || $[16] !== isUpcoming) {
    t13 = getWaveVotingState({
      isUpcoming,
      isCompleted
    });
    $[15] = isCompleted;
    $[16] = isUpcoming;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const votingState = t13;
  let t14;
  if ($[18] !== firstDecisionDone || $[19] !== hasAuthenticatedProfile || $[20] !== isChatWave || $[21] !== isCurationWave || $[22] !== isMemesWave || $[23] !== searchParams || $[24] !== updateAvailableTabs || $[25] !== votingState || $[26] !== wave.id) {
    t14 = () => {
      const hasSerialTarget = searchParams.get("serialNo") !== null;
      updateAvailableTabs({
        waveId: wave.id,
        isMemesWave,
        isChatWave,
        hasAuthenticatedProfile,
        isCurationWave,
        votingState,
        hasFirstDecisionPassed: firstDecisionDone,
        transientPreferredTab: hasSerialTarget ? MyStreamWaveTab.CHAT : null
      });
    };
    $[18] = firstDecisionDone;
    $[19] = hasAuthenticatedProfile;
    $[20] = isChatWave;
    $[21] = isCurationWave;
    $[22] = isMemesWave;
    $[23] = searchParams;
    $[24] = updateAvailableTabs;
    $[25] = votingState;
    $[26] = wave.id;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== firstDecisionDone || $[29] !== hasAuthenticatedProfile || $[30] !== isChatWave || $[31] !== isCurationWave || $[32] !== isMemesWave || $[33] !== searchParams || $[34] !== updateAvailableTabs || $[35] !== votingState || $[36] !== wave) {
    t15 = [wave, isMemesWave, isChatWave, hasAuthenticatedProfile, isCurationWave, votingState, firstDecisionDone, searchParams, updateAvailableTabs];
    $[28] = firstDecisionDone;
    $[29] = hasAuthenticatedProfile;
    $[30] = isChatWave;
    $[31] = isCurationWave;
    $[32] = isMemesWave;
    $[33] = searchParams;
    $[34] = updateAvailableTabs;
    $[35] = votingState;
    $[36] = wave;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[38] !== activeCurationId || $[39] !== setActiveContentTab || $[40] !== wave.wave.type) {
    t16 = () => {
      if (wave.wave.type === ApiWaveType.Chat && !activeCurationId) {
        setActiveContentTab(MyStreamWaveTab.CHAT);
      }
    };
    t17 = [wave.wave.type, activeCurationId, setActiveContentTab];
    $[38] = activeCurationId;
    $[39] = setActiveContentTab;
    $[40] = wave.wave.type;
    $[41] = t16;
    $[42] = t17;
  } else {
    t16 = $[41];
    t17 = $[42];
  }
  useEffect(t16, t17);
  let t18;
  if ($[43] !== availableTabs || $[44] !== hasAuthenticatedProfile || $[45] !== isCurationWave || $[46] !== isMemesWave) {
    let t19;
    if ($[48] !== hasAuthenticatedProfile || $[49] !== isCurationWave || $[50] !== isMemesWave) {
      t19 = tab => {
        if (tab === MyStreamWaveTab.MY_VOTES) {
          return isCurationWave || isMemesWave && hasAuthenticatedProfile;
        }
        if (tab === MyStreamWaveTab.SALES) {
          return isCurationWave;
        }
        if (tab === MyStreamWaveTab.FAQ) {
          return isMemesWave;
        }
        return true;
      };
      $[48] = hasAuthenticatedProfile;
      $[49] = isCurationWave;
      $[50] = isMemesWave;
      $[51] = t19;
    } else {
      t19 = $[51];
    }
    t18 = availableTabs.filter(t19).map(_temp3);
    $[43] = availableTabs;
    $[44] = hasAuthenticatedProfile;
    $[45] = isCurationWave;
    $[46] = isMemesWave;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const standardOptions = t18;
  let t19;
  if ($[52] !== curations) {
    t19 = curations.map(_temp4);
    $[52] = curations;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const curationOptions = t19;
  let t20;
  if ($[54] !== curationOptions || $[55] !== standardOptions) {
    t20 = [...standardOptions, ...curationOptions];
    $[54] = curationOptions;
    $[55] = standardOptions;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  const options = t20;
  const activeKey = activeCurationId ? `curation:${activeCurationId}` : activeTab;
  let t21;
  bb0: {
    if (curationOptions.length <= MOBILE_INLINE_CURATION_LIMIT) {
      t21 = curationOptions;
      break bb0;
    }
    let t22;
    if ($[57] !== activeKey || $[58] !== curationOptions) {
      t22 = curationOptions.find(option => option.key === activeKey) ?? null;
      $[57] = activeKey;
      $[58] = curationOptions;
      $[59] = t22;
    } else {
      t22 = $[59];
    }
    const activeCurationOption = t22;
    let visibleOptions;
    if ($[60] !== activeCurationOption || $[61] !== curationOptions) {
      visibleOptions = activeCurationOption ? [activeCurationOption] : [];
      for (const option_0 of curationOptions) {
        if (visibleOptions.length >= MOBILE_INLINE_CURATION_LIMIT) {
          break;
        }
        if (option_0.key === activeCurationOption?.key) {
          continue;
        }
        visibleOptions.push(option_0);
      }
      $[60] = activeCurationOption;
      $[61] = curationOptions;
      $[62] = visibleOptions;
    } else {
      visibleOptions = $[62];
    }
    t21 = visibleOptions;
  }
  const mobileVisibleCurationOptions = t21;
  let t22;
  if ($[63] !== mobileVisibleCurationOptions) {
    t22 = new Set(mobileVisibleCurationOptions.map(_temp5));
    $[63] = mobileVisibleCurationOptions;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  const visibleKeys = t22;
  let t23;
  if ($[65] !== curationOptions || $[66] !== visibleKeys) {
    let t24;
    if ($[68] !== visibleKeys) {
      t24 = option_2 => !visibleKeys.has(option_2.key);
      $[68] = visibleKeys;
      $[69] = t24;
    } else {
      t24 = $[69];
    }
    t23 = curationOptions.filter(t24);
    $[65] = curationOptions;
    $[66] = visibleKeys;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  const mobileOverflowCurationOptions = t23;
  let t24;
  if ($[70] !== mobileVisibleCurationOptions || $[71] !== standardOptions) {
    t24 = [...standardOptions, ...mobileVisibleCurationOptions];
    $[70] = mobileVisibleCurationOptions;
    $[71] = standardOptions;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  const mobileOptions = t24;
  let t25;
  if ($[73] !== mobileOverflowCurationOptions || $[74] !== onSelectCuration) {
    let t26;
    if ($[76] !== onSelectCuration) {
      t26 = option_3 => ({
        id: option_3.key,
        label: option_3.label,
        onSelect: () => onSelectCuration(option_3.key.replace("curation:", ""))
      });
      $[76] = onSelectCuration;
      $[77] = t26;
    } else {
      t26 = $[77];
    }
    t25 = mobileOverflowCurationOptions.map(t26);
    $[73] = mobileOverflowCurationOptions;
    $[74] = onSelectCuration;
    $[75] = t25;
  } else {
    t25 = $[75];
  }
  const mobileOverflowItems = t25;
  let t26;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => {
      const frameId = globalThis.window.requestAnimationFrame(() => {
        [desktopTabsScrollerRef.current, mobileTabsScrollerRef.current].forEach(_temp6);
      });
      return () => {
        globalThis.window.cancelAnimationFrame(frameId);
      };
    };
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  let t27;
  if ($[79] !== activeKey || $[80] !== options) {
    t27 = [activeKey, options];
    $[79] = activeKey;
    $[80] = options;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  useEffect(t26, t27);
  if (isChatWave && !canManageCurations && curations.length === 0) {
    return null;
  }
  let t28;
  if ($[82] !== onSelectCuration || $[83] !== setActiveTab) {
    t28 = key => {
      if (key.startsWith("curation:")) {
        onSelectCuration(key.replace("curation:", ""));
        return;
      }
      onSelectCuration(null);
      setActiveTab(key as MyStreamWaveTab);
    };
    $[82] = onSelectCuration;
    $[83] = setActiveTab;
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  let t29;
  if ($[85] !== activeKey || $[86] !== mobileOptions || $[87] !== t28) {
    t29 = <TabToggle options={mobileOptions} activeKey={activeKey} onSelect={t28} />;
    $[85] = activeKey;
    $[86] = mobileOptions;
    $[87] = t28;
    $[88] = t29;
  } else {
    t29 = $[88];
  }
  let t30;
  if ($[89] !== mobileOverflowItems) {
    t30 = mobileOverflowItems.length > 0 && <CompactMenu triggerClassName="tw-inline-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white" trigger={<EllipsisVerticalIcon className="tw-h-5 tw-w-5" />} aria-label="More curations" items={mobileOverflowItems} menuWidthClassName="tw-w-52" />;
    $[89] = mobileOverflowItems;
    $[90] = t30;
  } else {
    t30 = $[90];
  }
  let t31;
  if ($[91] !== t29 || $[92] !== t30) {
    t31 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1 sm:tw-hidden"><div ref={mobileTabsScrollerRef} className="tw-min-w-0 tw-flex-1 tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300"><div className="tw-inline-flex tw-items-center tw-gap-1">{t29}{t30}</div></div></div>;
    $[91] = t29;
    $[92] = t30;
    $[93] = t31;
  } else {
    t31 = $[93];
  }
  let t32;
  if ($[94] !== onSelectCuration || $[95] !== setActiveTab) {
    t32 = key_0 => {
      if (key_0.startsWith("curation:")) {
        onSelectCuration(key_0.replace("curation:", ""));
        return;
      }
      onSelectCuration(null);
      setActiveTab(key_0 as MyStreamWaveTab);
    };
    $[94] = onSelectCuration;
    $[95] = setActiveTab;
    $[96] = t32;
  } else {
    t32 = $[96];
  }
  let t33;
  if ($[97] !== activeKey || $[98] !== options || $[99] !== t32) {
    t33 = <div ref={desktopTabsScrollerRef} className="tw-hidden tw-min-w-0 tw-flex-1 tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300 sm:tw-block"><TabToggle options={options} activeKey={activeKey} onSelect={t32} /></div>;
    $[97] = activeKey;
    $[98] = options;
    $[99] = t32;
    $[100] = t33;
  } else {
    t33 = $[100];
  }
  let t34;
  if ($[101] !== onSelectCuration || $[102] !== showCreateCurationAction || $[103] !== wave) {
    t34 = showCreateCurationAction && <div className="sm:tw-ml-auto"><MyStreamWaveCreateCurationAction wave={wave} onCreated={onSelectCuration} /></div>;
    $[101] = onSelectCuration;
    $[102] = showCreateCurationAction;
    $[103] = wave;
    $[104] = t34;
  } else {
    t34 = $[104];
  }
  let t35;
  if ($[105] !== t31 || $[106] !== t33 || $[107] !== t34) {
    t35 = <div className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-2 tw-@container/tabs sm:tw-px-4">{t31}{t33}{t34}</div>;
    $[105] = t31;
    $[106] = t33;
    $[107] = t34;
    $[108] = t35;
  } else {
    t35 = $[108];
  }
  return t35;
};
export default MyStreamWaveDesktopTabs;
function _temp(decision) {
  return {
    decision_time: decision.timestamp
  };
}
function _temp2(decision_1) {
  return decision_1.timestamp > Time.currentMillis();
}
function _temp3(tab_0) {
  return {
    key: tab_0,
    label: TAB_LABELS[tab_0],
    panelId: getContentTabPanelId(tab_0)
  };
}
function _temp4(curation) {
  return {
    key: `curation:${curation.id}`,
    label: curation.name,
    panelId: getCurationPanelId(curation.id)
  };
}
function _temp5(option_1) {
  return option_1.key;
}
function _temp6(scroller) {
  if (!scroller) {
    return;
  }
  const activeTabElement = scroller.querySelector("[role=\"tab\"][aria-selected=\"true\"]");
  activeTabElement?.scrollIntoView({
    block: "nearest",
    inline: "nearest"
  });
}